import React, { useState, useEffect, useCallback } from "react";
import "../css/ClustalOmega.css";

const SUPPORTED_FORMATS = ["clustal", "fasta", "msf", "phylip", "selex", "stockholm"];

export default function ClustalOmegaAlignment() {
  const [file, setFile] = useState(null);
  const [sequenceText, setSequenceText] = useState("");
  const [seqtype, setSeqtype] = useState("Auto");
  const [threads, setThreads] = useState(4);
  const [title, setTitle] = useState("Clustal Omega Job");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState("idle");
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState(0);
  const [selectedResultIdx, setSelectedResultIdx] = useState(0);
  const [selectedFormat, setSelectedFormat] = useState("clustal");
  const [showColor, setShowColor] = useState(false);
  const [showTree, setShowTree] = useState(false);
  const [treeText, setTreeText] = useState(null);

  // Correct sequence type detection
  const detectSeqType = useCallback((seq) => {
    const cleanSeq = seq.replace(/^>.*$/gm, "").replace(/\s/g, "");
    if (!cleanSeq) return "Auto";

    const dnaMatch = cleanSeq.match(/[^ACGTURYKMSWBDHVN\-.]/gi);
    if (!dnaMatch) return "DNA";

    const protMatch = cleanSeq.match(/[^ACDEFGHIKLMNPQRSTVWYBXZ\-.]/gi);
    if (!protMatch) return "PROT";

    return "Auto";
  }, []);

  useEffect(() => {
    if (!sequenceText) return;
    const detected = detectSeqType(sequenceText);
    if (seqtype === "Auto") setSeqtype(detected);
  }, [sequenceText, seqtype, detectSeqType]);

  const validateSequence = useCallback(() => {
    if (!sequenceText.trim() && !file) {
      alert("Please paste a sequence or upload a FASTA file.");
      return false;
    }
    return true;
  }, [sequenceText, file]);

  const submit = async () => {
    if (!validateSequence()) return;
    setMessage("⏳ Processing alignment...");
    setErrorMessage("");
    setResults([]);
    setStatus("PENDING");
    setProgress(10);

    const form = new FormData();
    if (file) form.append("sequences", file);
    else form.append("sequence_text", sequenceText);
    form.append("seqtype", seqtype === "Auto" ? detectSeqType(sequenceText) : seqtype);
    form.append("threads", threads.toString());
    form.append("title", title || "Untitled Alignment");

    try {
      const res = await fetch("/api/alignments/", { method: "POST", body: form });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setJobId(data.job_id);
      setMessage("✅ Job submitted successfully!");
      setStatus("RUNNING");
    } catch (err) {
      setErrorMessage("Submission failed: " + err.message);
      setMessage("");
      setStatus("FAILED");
    }
  };

  // Polling job status
  useEffect(() => {
    if (!jobId || !["PENDING", "RUNNING"].includes(status)) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/alignments/status/${jobId}/`);
        const data = await res.json();
        setStatus(data.status);
        setProgress(data.progress || 0);
        if (data.status === "DONE") {
          setResults(data.results || []);
          if (data.results?.[0]?.tree_text) setTreeText(data.results[0].tree_text);
          clearInterval(interval);
          setMessage("✅ Alignment completed successfully.");
        } else if (data.status === "FAILED") {
          setErrorMessage("❌ Alignment failed.");
          clearInterval(interval);
        }
      } catch {
        setErrorMessage("Error checking job status.");
        clearInterval(interval);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [jobId, status]);

  const handleBack = () => {
    setJobId(null);
    setStatus("idle");
    setResults([]);
    setMessage("");
    setErrorMessage("");
    setProgress(0);
    setSelectedFormat("clustal");
    setFile(null);
    setSequenceText("");
    setSeqtype("Auto");
    setTitle("Clustal Omega Job");
    setSelectedResultIdx(0);
    setShowColor(false);
    setTreeText(null);
    setShowTree(false);
  };

  const colorizeAlignment = (text) => {
    if (!text) return null;
    const colorMap =
      seqtype === "DNA"
        ? { A: "green", T: "red", C: "blue", G: "orange", "-": "gray" }
        : {
            A: "red", R: "green", N: "blue", D: "orange", C: "purple",
            Q: "teal", E: "brown", G: "pink", H: "cyan", I: "magenta",
            L: "lime", K: "yellow", M: "violet", F: "salmon", P: "gold",
            S: "coral", T: "navy", W: "darkgreen", Y: "darkred", V: "darkblue", "-": "gray"
          };

    return (
      <div>
        {text.split("\n").map((line, idx) => (
          <div key={idx} style={{ fontFamily: "monospace" }}>
            {line.split("").map((ch, i) => (
              <span key={i} style={{ color: colorMap[ch.toUpperCase()] || "black" }}>
                {ch}
              </span>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const downloadAlignment = (res) => {
    const alignmentText = res[`${selectedFormat}_text`] || res.alignment;
    const blob = new Blob([alignmentText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${res.title || "alignment"}.${selectedFormat}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isColorable = ["clustal", "msf", "stockholm"].includes(selectedFormat);

  return (
    <div className="clustal-omega-container">
      <a href="https://www.ebi.ac.uk/Tools/msa/clustalo/" target="_blank" rel="noopener noreferrer">
        EMBL Clustal Omega
      </a>
      <h2 className="page-title">🧬 Clustal Omega — Multiple Sequence Alignment (MSA)</h2>

      {message && <div className="message info">{message}</div>}
      {errorMessage && <div className="message error">{errorMessage}</div>}

      {status === "idle" && (
        <div className="input-section">
          <label>Job Title:</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} />

          <div className="sequence-box">
            <label>Paste sequences (FASTA format):</label>
            <textarea
              value={sequenceText}
              onChange={(e) => setSequenceText(e.target.value)}
            />
            <p>OR upload a FASTA file:</p>
            <input
              type="file"
              accept=".fasta,.fa,.txt"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </div>

          <div className="options-box">
            <label>Sequence Type:</label>
            <select value={seqtype} onChange={(e) => setSeqtype(e.target.value)}>
              <option value="Auto">Auto-detect</option>
              <option value="DNA">DNA</option>
              <option value="PROT">Protein</option>
            </select>

            <label>Threads:</label>
            <input
              type="number"
              value={threads}
              onChange={(e) => setThreads(parseInt(e.target.value))}
              min={1}
            />
          </div>

          <button className="submit-btn" onClick={submit}>
            Run Alignment
          </button>
        </div>
      )}

      {(status === "PENDING" || status === "RUNNING") && (
        <div className="progress-box">
          <p>Status: <strong>{status}</strong></p>
          <progress value={progress} max={100} />
        </div>
      )}

      {status === "DONE" && results.length > 0 && (
        <div className="results-section">
          <h3>Alignment Results</h3>

          <pre className="metadata-header">
            {`Title: ${title}
Program: Clustal Omega
Job ID: ${jobId}
Sequence Type: ${seqtype}
Output Format: ${selectedFormat.toUpperCase()}
Status: ${status}`}
          </pre>

          <div className="format-tabs">
            {SUPPORTED_FORMATS.map((fmt) => (
              <button
                key={fmt}
                className={fmt === selectedFormat ? "active" : ""}
                onClick={() => setSelectedFormat(fmt)}
              >
                {fmt.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="alignment-text">
            {showColor && isColorable
              ? colorizeAlignment(results[selectedResultIdx][`${selectedFormat}_text`])
              : <pre>{results[selectedResultIdx][`${selectedFormat}_text`]}</pre>}
          </div>

          <div className="buttons-row">
            <button
              className="toggle-color-btn"
              onClick={() => setShowColor(!showColor)}
              disabled={!isColorable}
            >
              🎨 {showColor ? "Show Plain" : "Show Colors"}
            </button>

            {treeText && (
              <button
                className="toggle-tree-btn"
                onClick={() => setShowTree(!showTree)}
              >
                🌳 {showTree ? "Hide Tree" : "Show Tree"}
              </button>
            )}

            <button
              className="download-btn"
              onClick={() => downloadAlignment(results[selectedResultIdx])}
            >
              💾 Download
            </button>

            <button className="back-btn" onClick={handleBack}>
              🔙 New Job
            </button>
          </div>

          {treeText && showTree && (
            <div className="tree-container">
              <h4>🌳 Phylogenetic Tree</h4>
              <pre className="newick-box">{treeText}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

